import { useI18N } from "@sundae/react-hooks";
import {
  Balance,
  ConditionalWrapper,
  FractionInput,
  InfoCircledIcon,
  NumberI18N,
  Text,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { IMappedAsset } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import cx from "classnames";
import { FC, useMemo, useState } from "react";

import { DEFAULT_DECIMALS } from "../../constants/cardano.constants";
import { DEFAULT_INPUT_PLACEHOLDER } from "../../constants/exchange.constants";
import { FRACTION_INPUT_MAX_LENGTH } from "../../constants/page.constants";
import { testIds } from "../../constants/testIds.constants";
import { useAdaBalance } from "../../hooks/useAdaBalance";
import { useAdaData } from "../../hooks/useAdaData";
import { useProfileAppContext } from "../../stores";
import { EOrderTransactionBuilderState, useOrderContext } from "../../stores/OrderContext";
import { IAssetMetaData } from "../../types/Asset.types";
import { assetHasQuantity } from "../../utils/assets.utils";
import AssetSelectTrigger from "../AssetSelectTrigger";
import AvailableADABalanceTooltip from "../AvailableADABalanceTooltip/AvailableADABalanceTooltip";
import SwapMaxButton from "../SwapMaxButton";

export interface IAssetAmountBoxProps {
  activeAsset?: IMappedAsset<IAssetMetaData> | null;
  assets?: IMappedAsset<IAssetMetaData>[];
  assetValue: string;
  autoFocus?: boolean;
  className?: string;
  dialogTitle?: string;
  disabled?: boolean;
  prevValue?: string;
  isLoading?: boolean;
  isSelectDisabled?: boolean;
  setActiveAsset: (_asset: IMappedAsset<IAssetMetaData>) => void;
  setValueAsset(value: string): void;
  testId?: string;
  topline?: string;
}

const AssetAmountBox: FC<IAssetAmountBoxProps> = ({
  activeAsset,
  assets = [],
  assetValue,
  autoFocus,
  className,
  dialogTitle,
  disabled,
  prevValue,
  isLoading,
  isSelectDisabled = false,
  setActiveAsset,
  setValueAsset,
  testId = testIds.assetAmountBox.container,
  topline,
}) => {
  const { t, currentLocale, decimalSeparator, thousandSeparator } = useI18N("exchange");
  const { data: adaData } = useAdaData();
  const [active, setActive] = useState(false);
  const { availableAdaBalance } = useAdaBalance();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const {
    state: {
      derived: { transaction },
    },
  } = useOrderContext();

  // prevValue is only pass when is updating an order and is equal to swapQuantityA or swapQuantityB, so prevAssetAmount is basically prevValue as AssetAmount
  const prevAssetAmount = useMemo(
    () => new AssetAmount(prevValue ?? 0n, activeAsset?.metadata ?? DEFAULT_DECIMALS),
    [prevValue, activeAsset]
  );
  const isAssetADA = !!activeAsset && SundaeUtils.isAdaAsset(activeAsset?.metadata);
  const maxAmount = useMemo(() => {
    const amount =
      (isAssetADA ? availableAdaBalance?.quantity : activeAsset?.quantity) ??
      new AssetAmount(0n, activeAsset?.metadata ?? DEFAULT_DECIMALS);
    return amount;
  }, [
    activeAsset?.metadata,
    activeAsset?.quantity,
    isAssetADA,
    availableAdaBalance,
    prevAssetAmount,
  ]);
  const isInsufficient = useMemo(() => {
    return Number(assetValue) > maxAmount.value.toNumber();
  }, [assetValue, maxAmount.value]);

  const handleChange = (value: string) => {
    setValueAsset(value);
  };

  return (
    <div
      className={cx(
        "grid min-h-[6.4rem] gap-4 rounded-lg border-transparent",
        toolkitCx.layout.transition.colors,
        toolkitCx.layout.background.card,
        toolkitCx.layout.border.hoverable,
        toolkitCx.layout.border.flashOnFocus,
        toolkitCx.layout.border.weight.default,
        toolkitCx.layout.spacing.both.md,
        {
          "!border-primary-200/70 dark:!border-primary-200/70": active,
        }
      )}
      data-testid={testId}
    >
      <div className="grid gap-2">
        {topline && (
          <Text tag="span" size="xs" variant="silent">
            {topline}
          </Text>
        )}
        <div className={cx("grid grid-cols-[1fr_auto] items-center gap-4", className)}>
          <div className="flex w-full items-center justify-end overflow-hidden">
            <FractionInput
              autoFocus={autoFocus}
              key={activeAsset?.metadata?.assetId}
              className={cx("w-full flex-grow text-3xl ", {
                "motion-safe:animate-shake": isInsufficient && activeAsset && !isLoading,
                [`${toolkitCx.layout.text.silent} animate-pulse cursor-not-allowed`]:
                  isLoading || transaction?.state === EOrderTransactionBuilderState.BUILD,
                [`${toolkitCx.layout.text.default}`]: !isLoading,
              })}
              data-testid={testIds.assetAmountBox.input}
              decimalScale={activeAsset?.metadata?.decimals}
              decimalSeparator={decimalSeparator}
              disabled={isLoading || !activeAsset || disabled}
              maxLength={FRACTION_INPUT_MAX_LENGTH}
              onValueChange={({ value }) => {
                if (value !== assetValue) handleChange(value);
              }}
              onBlur={() => setActive(false)}
              onFocus={() => setActive(true)}
              placeholder={DEFAULT_INPUT_PLACEHOLDER}
              thousandSeparator={thousandSeparator}
              value={assetValue === "0" ? undefined : assetValue}
              {...(transaction?.state === EOrderTransactionBuilderState.BUILD && {
                onKeyDown: (e) => {
                  e.preventDefault();
                  return;
                },
              })}
              {...(!active && {
                style: {
                  WebkitMaskImage:
                    "linear-gradient(to left, transparent, rgb(0, 0, 0) 7%, rgb(0, 0, 0) 100%, transparent)",
                },
              })}
            />
          </div>

          {activeAsset && (
            <AssetSelectTrigger
              activeAsset={new AssetAmount(activeAsset.quantity.amount, activeAsset.metadata)}
              assets={assets}
              dialogTitle={dialogTitle}
              isCompact
              isDisabled={isSelectDisabled}
              isLoading={isLoading}
              onAssetSelect={(asset) => setActiveAsset(asset)}
            />
          )}
        </div>
      </div>

      <div className="flex justify-between">
        <>
          {isLoading ? (
            <div
              className={cx(
                "h-[18px] w-28 rounded-lg motion-safe:animate-pulse",
                toolkitCx.layout.background.loading
              )}
            />
          ) : (
            isAssetADA &&
            adaData &&
            assetValue && (
              <Text testId={testIds.assetAmountBox.usdValue} tag="div" size="xs" variant="silent">
                {!privacyEnabled && "~ "}
                <NumberI18N
                  formatOpts={{
                    currency: "USD",
                    currencyDisplay: "narrowSymbol",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    style: "currency",
                  }}
                  locale={currentLocale}
                  privacy={privacyEnabled}
                  size="xs"
                  value={adaData.usd * Number(assetValue)}
                  variant="silent"
                />
              </Text>
            )
          )}
        </>

        {isLoading ? (
          <div
            className={cx(
              "h-[18px] w-28 rounded-lg motion-safe:animate-pulse",
              toolkitCx.layout.background.loading
            )}
          />
        ) : (
          <Text
            tag="div"
            size="xs"
            className="flex flex-1 items-center justify-end gap-1 text-right"
            variant="silent"
          >
            <ConditionalWrapper
              condition={isAssetADA}
              wrapper={(children) => (
                <AvailableADABalanceTooltip>{children}</AvailableADABalanceTooltip>
              )}
            >
              <Text
                className="flex items-center"
                tag="span"
                size="xs"
                variant={isInsufficient && !isLoading ? "warning" : "silent"}
              >
                {t("balance", { ns: "glossary", context: "available" })}
                {isAssetADA && (
                  <InfoCircledIcon
                    className={cx("mx-[2px] h-3 w-3", {
                      [`${toolkitCx.layout.text.silent}`]: !isInsufficient,
                      [`${toolkitCx.layout.text.warning}`]: isInsufficient,
                    })}
                  />
                )}
                :
              </Text>
            </ConditionalWrapper>{" "}
            <Balance
              abbreviate={false}
              locale={currentLocale}
              privacy={privacyEnabled}
              showSymbol={!!activeAsset && SundaeUtils.isAdaAsset(activeAsset?.metadata)}
              size="xs"
              testId={testIds.assetAmountBox.balance}
              value={maxAmount}
              variant={isInsufficient ? "warning" : "silent"}
            />
            {assetHasQuantity(activeAsset) && (
              <SwapMaxButton assetValue={assetValue} maxAmount={maxAmount} onClick={handleChange} />
            )}
          </Text>
        )}
      </div>
    </div>
  );
};

export default AssetAmountBox;
